import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, ShieldAlert } from "lucide-react";

import { confirmTotp, disableTotp, regenerateRecoveryCodes, startTotp, type TOTPEnrollment } from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { useToast } from "@/components/ui/toast";
import { CopyButton, Spinner } from "@/components/Page";

// RecoveryCodes is the one-time display of recovery codes after enable or
// regenerate. They're shown exactly once — the server only stores hashes.
function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm text-warning">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <p>Save these recovery codes somewhere safe. Each works once, and they won't be shown again.</p>
      </div>
      <div className="grid grid-cols-2 gap-2 rounded-lg border bg-muted/30 p-3 font-mono text-sm">
        {codes.map((c) => (
          <span key={c} className="select-all">
            {c}
          </span>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <CopyButton value={codes.join("\n")} />
        <Button onClick={onDone}>I've saved them</Button>
      </div>
    </div>
  );
}

// SetupDialog walks enrollment: scan the QR (or enter the secret), confirm a
// code, then save the recovery codes.
function SetupDialog({ open, onOpenChange, onEnabled }: { open: boolean; onOpenChange: (o: boolean) => void; onEnabled: () => void }) {
  const toast = useToast();
  const [enroll, setEnroll] = useState<TOTPEnrollment | null>(null);
  const [step, setStep] = useState<"scan" | "codes">("scan");
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // begin (re)starts enrollment whenever the dialog opens.
  function onOpen(o: boolean) {
    onOpenChange(o);
    if (!o) return;
    setEnroll(null);
    setStep("scan");
    setCode("");
    setCodes([]);
    setErr("");
    startTotp()
      .then(setEnroll)
      .catch((e) => setErr(humanizeError(e, "Couldn't start setup. Please try again.")));
  }

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await confirmTotp(code.trim());
      setCodes(r.recovery_codes);
      setStep("codes");
    } catch (e2) {
      setErr(humanizeError(e2, "That code didn't match. Try again."));
    }
    setBusy(false);
  }

  function finish() {
    onOpenChange(false);
    toast.success("Two-step verification is on");
    onEnabled();
  }

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{step === "codes" ? "Save your recovery codes" : "Set up two-step verification"}</DialogTitle>
          {step === "scan" && (
            <DialogDescription>
              Scan this with an authenticator app (Google Authenticator, 1Password, Authy…), then enter the code it
              shows.
            </DialogDescription>
          )}
        </DialogHeader>

        {step === "codes" ? (
          <RecoveryCodes codes={codes} onDone={finish} />
        ) : !enroll ? (
          err ? (
            <p className="text-sm text-destructive">{err}</p>
          ) : (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6" />
            </div>
          )
        ) : (
          <form onSubmit={confirm} className="space-y-4">
            <div className="flex justify-center">
              <div className="rounded-lg bg-white p-3">
                <QRCodeSVG value={enroll.otpauth_url} size={168} />
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Or enter this key manually:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 break-all rounded-md border bg-muted/30 px-2 py-1.5 font-mono text-xs">
                  {enroll.secret}
                </code>
                <CopyButton value={enroll.secret} />
              </div>
            </div>
            <Field label="Code from your app" htmlFor="totp-confirm" error={err}>
              <Input
                id="totp-confirm"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                maxLength={6}
                aria-invalid={!!err}
              />
            </Field>
            <DialogFooter>
              <Button type="submit" disabled={busy || code.trim().length < 6}>
                {busy ? <Spinner /> : null} {busy ? "Verifying…" : "Turn on"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// CodeDialog asks for a current code to authorize a sensitive change
// (disable, or regenerate recovery codes). onSubmit resolves with the new
// recovery codes for regenerate, or undefined for disable.
function CodeDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive,
  onSubmit,
  onDone,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  onSubmit: (code: string) => Promise<string[] | void>;
  onDone: (codes?: string[]) => void;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);

  function reset(o: boolean) {
    onOpenChange(o);
    if (o) {
      setCode("");
      setErr("");
      setBusy(false);
      setCodes(null);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const result = await onSubmit(code.trim());
      if (result && result.length) {
        setCodes(result);
      } else {
        onOpenChange(false);
        onDone();
      }
    } catch (e2) {
      setErr(humanizeError(e2, "That code didn't match. Try again."));
    }
    setBusy(false);
  }

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{codes ? "Save your recovery codes" : title}</DialogTitle>
          {!codes && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {codes ? (
          <RecoveryCodes
            codes={codes}
            onDone={() => {
              onOpenChange(false);
              onDone(codes);
            }}
          />
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Field label="Authenticator or recovery code" htmlFor="code-confirm" error={err}>
              <Input
                id="code-confirm"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoComplete="one-time-code"
                placeholder="123456"
                aria-invalid={!!err}
                autoFocus
              />
            </Field>
            <DialogFooter>
              <Button type="submit" variant={destructive ? "destructive" : "default"} disabled={busy || !code.trim()}>
                {busy ? <Spinner /> : null} {busy ? "Verifying…" : confirmLabel}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// TwoFactorSection is the Profile security control for TOTP 2FA. onChanged
// refetches /v1/me so the enabled state stays in sync.
export function TwoFactorSection({ enabled, onChanged }: { enabled: boolean; onChanged: () => void }) {
  const toast = useToast();
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);

  return (
    <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3">
      <ShieldCheck className={`mt-0.5 h-4 w-4 shrink-0 ${enabled ? "text-success" : "text-muted-foreground"}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">Two-step verification</span>
          {enabled ? (
            <Badge variant="outline" className="border-success/30 bg-success/10 text-success">
              On
            </Badge>
          ) : (
            <Badge variant="outline" className="text-muted-foreground">
              Off
            </Badge>
          )}
        </div>
        <p className="text-muted-foreground">
          {enabled
            ? "You'll enter a code from your authenticator app each time you sign in."
            : "Add a one-time code from an authenticator app on top of your email or SSO sign-in."}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {enabled ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setRegenOpen(true)}>
                Regenerate recovery codes
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDisableOpen(true)}>
                Turn off
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={() => setSetupOpen(true)}>
              Set up
            </Button>
          )}
        </div>
      </div>

      <SetupDialog open={setupOpen} onOpenChange={setSetupOpen} onEnabled={onChanged} />
      <CodeDialog
        open={disableOpen}
        onOpenChange={setDisableOpen}
        title="Turn off two-step verification"
        description="Enter a current authenticator code (or a recovery code) to confirm."
        confirmLabel="Turn off"
        destructive
        onSubmit={async (code) => {
          await disableTotp(code);
        }}
        onDone={() => {
          toast.success("Two-step verification turned off");
          onChanged();
        }}
      />
      <CodeDialog
        open={regenOpen}
        onOpenChange={setRegenOpen}
        title="Regenerate recovery codes"
        description="Enter a current authenticator code (or a recovery code). This replaces all your existing recovery codes."
        confirmLabel="Regenerate"
        onSubmit={async (code) => (await regenerateRecoveryCodes(code)).recovery_codes}
        onDone={() => onChanged()}
      />
    </div>
  );
}
